import { Button } from '@base-ui/react/button';
import { Dialog } from '@base-ui/react/dialog';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import buttonStyles from '~/styles/button.module.css';
import { ErrorText, FieldHint, FormLabel, FormRow, TextInput } from './Form';
import { FORMAT_HINT, useMediaUpload } from './MediaPicker';

// Upload an image into the text, or point at one already online.
export function ImageInsertDialog({
  open,
  onInsert,
  onClose,
}: {
  open: boolean;
  onInsert: (src: string) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const { upload, uploading, error, clearError } = useMediaUpload((image) => {
    // The resolved URL goes in the HTML; the save normalises it back to a
    // portable `{{MEDIA}}` token.
    onInsert(image.url);
    reset();
  });

  const reset = () => {
    setUrl('');
    clearError();
  };

  const close = () => {
    reset();
    onClose();
  };

  const trimmed = url.trim();
  const insertUrl = () => {
    if (!trimmed) return;
    onInsert(trimmed);
    reset();
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !uploading) close();
      }}
    >
      <Dialog.Portal>
        <Backdrop />
        <Popup>
          <Title>Insert image</Title>
          <FormRow>
            <input
              ref={input}
              type="file"
              aria-label="Image file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) upload(file);
              }}
            />
            <Button
              className={buttonStyles.Button}
              disabled={uploading}
              onClick={() => {
                clearError();
                input.current?.click();
              }}
            >
              {uploading ? 'Uploading...' : 'Upload image'}
            </Button>
            {error && <ErrorText role="alert">{error}</ErrorText>}
            <FieldHint>{FORMAT_HINT}</FieldHint>
          </FormRow>
          <Divider>or</Divider>
          <FormRow>
            <FormLabel htmlFor="image-url">Image URL</FormLabel>
            <TextInput
              id="image-url"
              value={url}
              placeholder="https://"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUrl(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') insertUrl();
              }}
            />
          </FormRow>
          <Actions>
            <Button
              className={buttonStyles.Button}
              disabled={uploading}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              className={buttonStyles.Button}
              disabled={!trimmed || uploading}
              onClick={insertUrl}
            >
              Insert URL
            </Button>
          </Actions>
        </Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const Backdrop = styled(Dialog.Backdrop)`
  background-color: rgba(0, 0, 0, 0.5);
  inset: 0;
  position: fixed;
  z-index: 100;
`;

const Popup = styled(Dialog.Popup)`
  background-color: var(--component-color);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-popover);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  left: 50%;
  max-width: 90vw;
  padding: 1.5rem;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 420px;
  z-index: 101;
`;

const Title = styled(Dialog.Title)`
  color: var(--grey-900);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

const Divider = styled.div`
  color: var(--grey-600);
  font-size: 0.8125rem;
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;
